/**
 * Wave 29 Chunk 5 — PUBLIC sign page (no auth).
 *
 * Anyone with a valid signing token can review the contract, draw their
 * signature on a canvas, type their full name, and submit. Server stamps
 * IP / UA / SHA-256 hash for audit.
 */
import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { FileSignature, CheckCircle2, Loader2, AlertTriangle } from "lucide-react";
import axios from "axios";
import { BACKEND_URL } from "../lib/api";

const API_BASE = `${BACKEND_URL}/api`;

export default function PublicSign() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [loading, setLoading] = useState(true);
  const [name, setName] = useState("");
  const [agreed, setAgreed] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(null);
  const canvasRef = useRef(null);
  const drawing = useRef(false);
  const hasInk = useRef(false);

  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API_BASE}/sign/${token}`);
        setData(r.data);
        if (r.data.signer?.name) setName(r.data.signer.name);
      } catch (e) {
        setErr(e?.response?.data?.detail || "Invalid signing link.");
      } finally { setLoading(false); }
    })();
  }, [token]);

  const startDraw = (e) => {
    drawing.current = true;
    const c = canvasRef.current; if (!c) return;
    const ctx = c.getContext("2d");
    const rect = c.getBoundingClientRect();
    const x = (e.touches?.[0]?.clientX ?? e.clientX) - rect.left;
    const y = (e.touches?.[0]?.clientY ?? e.clientY) - rect.top;
    ctx.beginPath();
    ctx.moveTo(x, y);
  };
  const draw = (e) => {
    if (!drawing.current) return;
    e.preventDefault();
    const c = canvasRef.current; if (!c) return;
    const ctx = c.getContext("2d");
    const rect = c.getBoundingClientRect();
    const x = (e.touches?.[0]?.clientX ?? e.clientX) - rect.left;
    const y = (e.touches?.[0]?.clientY ?? e.clientY) - rect.top;
    ctx.lineWidth = 2.5;
    ctx.lineCap = "round";
    ctx.strokeStyle = "#0e1015";
    ctx.lineTo(x, y);
    ctx.stroke();
    hasInk.current = true;
  };
  const stopDraw = () => { drawing.current = false; };
  const clearCanvas = () => {
    const c = canvasRef.current; if (!c) return;
    c.getContext("2d").clearRect(0, 0, c.width, c.height);
    hasInk.current = false;
  };

  const submit = async () => {
    if (!hasInk.current) { alert("Please draw your signature."); return; }
    if (!name.trim()) { alert("Type your full name."); return; }
    if (!agreed) { alert("Confirm you agree to the terms."); return; }
    setSubmitting(true);
    try {
      const dataUrl = canvasRef.current.toDataURL("image/png");
      const r = await axios.post(`${API_BASE}/sign/${token}`, {
        full_name: name.trim(),
        signature_data_url: dataUrl,
        agreed: true,
      });
      setDone(r.data);
    } catch (e) {
      alert(e?.response?.data?.detail || "Submission failed.");
    } finally { setSubmitting(false); }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center text-white p-6">
        <Loader2 className="w-6 h-6 animate-spin" />
      </div>
    );
  }
  if (err) {
    return (
      <div className="min-h-screen flex items-center justify-center text-white p-6">
        <div className="hv-card p-6 max-w-md text-center">
          <AlertTriangle className="w-8 h-8 text-amber-400 mx-auto mb-2" />
          <h1 className="font-display text-lg font-bold">Can't sign here</h1>
          <p className="text-sm text-[#a0aabf] mt-1">{err}</p>
        </div>
      </div>
    );
  }
  if (done || data?.already_signed) {
    return (
      <div className="min-h-screen flex items-center justify-center text-white p-6" data-testid="sign-success">
        <div className="hv-card p-8 max-w-md text-center">
          <CheckCircle2 className="w-12 h-12 text-emerald-400 mx-auto mb-3" />
          <h1 className="font-display text-xl font-bold">Signed</h1>
          <p className="text-sm text-[#a0aabf] mt-1">
            Thank you. A tamper-evident audit record has been stored.
          </p>
          {done?.audit_hash && (
            <p className="text-[10px] text-[#647087] font-mono mt-3 break-all">
              SHA-256: {done.audit_hash}
            </p>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen text-white p-4 sm:p-8" data-testid="public-sign-page">
      <div className="max-w-2xl mx-auto space-y-4">
        <div className="flex items-center gap-2 text-[#1FB6C1]">
          <FileSignature className="w-5 h-5" />
          <span className="text-[10px] uppercase tracking-[0.2em]">Sign · {data.contract?.kind}</span>
        </div>
        <h1 className="font-display text-2xl sm:text-3xl font-bold">{data.contract?.title}</h1>
        <div className="hv-card p-4">
          <pre className="text-sm text-[#cfd6e3] whitespace-pre-wrap leading-relaxed font-sans" data-testid="sign-contract-body">{data.contract?.body}</pre>
        </div>

        <div className="hv-card p-4 space-y-3">
          <label className="block text-xs text-[#a0aabf]">
            Your full legal name
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="hv-input mt-1 text-sm w-full"
              placeholder="e.g. Jane Doe"
              data-testid="sign-name"
            />
          </label>
          <div>
            <div className="text-xs text-[#a0aabf] mb-1">Draw your signature</div>
            <canvas
              ref={canvasRef}
              width={600}
              height={180}
              className="w-full bg-white rounded border border-white/10 touch-none cursor-crosshair"
              onMouseDown={startDraw}
              onMouseMove={draw}
              onMouseUp={stopDraw}
              onMouseLeave={stopDraw}
              onTouchStart={startDraw}
              onTouchMove={draw}
              onTouchEnd={stopDraw}
              data-testid="sign-canvas"
            />
            <button type="button" onClick={clearCanvas} className="text-[10px] text-[#647087] hover:text-white mt-1" data-testid="sign-clear">
              Clear signature
            </button>
          </div>
          <label className="inline-flex items-start gap-2 cursor-pointer text-xs text-[#a0aabf]">
            <input
              type="checkbox"
              checked={agreed}
              onChange={(e) => setAgreed(e.target.checked)}
              className="mt-0.5"
              data-testid="sign-agree"
            />
            <span>I have read the document above and confirm my signature is binding under South African law.</span>
          </label>
          <button onClick={submit} disabled={submitting} className="hv-btn hv-btn-primary text-sm w-full disabled:opacity-50" data-testid="sign-submit">
            {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileSignature className="w-4 h-4" />} Sign now
          </button>
          <p className="text-[10px] text-[#647087] text-center">
            Your IP, browser, and signature will be stored with a SHA-256 audit hash.
          </p>
        </div>
      </div>
    </div>
  );
}
